import { ChangeEvent } from 'react';
import { Box, IconButton, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  getPersonalDataTableRevoke,
  getPersonalDataTableSubmit,
} from '~/store/processing-personal/thunks/personalDataTableThunks.ts';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { DEFAULT_PERSONAL_DATA_TABLE_PAGINATION } from '../constants.ts';
import { styles } from '../../styles';
import { SearchParams, TSearchDataTableValues } from '../types.ts';

interface ISearchDataTableProps {
  onLoading?: (loading: boolean) => void;
}

export const SearchDataTable = ({ onLoading }: ISearchDataTableProps) => {
  const dispatch = useAppDispatch();

  const formMethods = useForm<TSearchDataTableValues>({
    defaultValues: {
      search: '',
    },
  });

  const fetchData = async (data: SearchParams = {}) => {
    const requestData = {
      ...DEFAULT_PERSONAL_DATA_TABLE_PAGINATION,
      ...data,
    };

    try {
      onLoading?.(true);
      await Promise.all([
        dispatch(getPersonalDataTableSubmit({ data: requestData })).unwrap(),
        dispatch(getPersonalDataTableRevoke({ data: requestData })).unwrap(),
      ]);
    } catch (err) {
      console.error('SearchDataTable fetchData error:', err);
    } finally {
      onLoading?.(false);
    }
  };

  const handleSubmitSearch: SubmitHandler<TSearchDataTableValues> = ({ search }) => {
    const trimmed = (search || '').trim();

    if (trimmed) {
      void fetchData({ search: trimmed });
    }
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!e.target.value.trim()) {
      void fetchData();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Box component="form" onSubmit={formMethods.handleSubmit(handleSubmitSearch)}>
        <FormInput
          name="search"
          onChange={handleSearchChange}
          placeholder="Введіть параметри для пошуку"
          variant="outlined"
          sx={styles.tableSearchField}
          inputProps={{ 'data-testid': 'personal-data-search-input' }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton type="submit" edge="end" aria-label="search" color="primary">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
    </FormProvider>
  );
};
